import React, { useState, useEffect } from 'react';
import Header from '../../Layout/Header';
import Sidebar from '../../Layout/Sidebar';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { SERVER_URL } from '../../../config';

export default function Report() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Fetch reports on mount
    useEffect(() => {
        const fetchReports = async () => {
            try {
                const res = await axios.get(`${SERVER_URL}/api/admin/uploaded-reports`);
                if (res.data.status) {
                    setData(res.data.data);
                } else {
                    console.error('Failed to load reports');
                }
            } catch (err) {
                console.error('Error fetching report data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchReports();
    }, []);

    const filteredData = data.filter((item) =>
        item.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.extracted_id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);
    const emptyRows = rowsPerPage - currentData.length;

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages || 1);
        }
    }, [rowsPerPage, totalPages]);

    const handlePrev = () => currentPage > 1 && setCurrentPage(currentPage - 1);
    const handleNext = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
    const handleRowsChange = (e) => {
        setRowsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    return (
        <div className='min-h-screen lg:h-screen overflow-y-auto flex flex-col bg-cover bg-no-repeat text-white' style={{ backgroundImage: 'url("/assets/bg/adminbg.svg")' }}>
            <Header />
            <div className='flex flex-col lg:flex-row flex-grow overflow-hidden'>
                <Sidebar />
                <div className='lg:w-[80%] overflow-y-auto p-5'>
                    <div className='flex flex-col lg:flex-row justify-between gap-4 mb-4'>
                        <p className='text-xl font-bold'>Player Reports</p>
                        <div className='flex gap-3 items-center'>
                            <select
                                value={rowsPerPage}
                                onChange={handleRowsChange}
                                className='bg-white text-black px-3 py-2 rounded-md outline-none'
                            >
                                <option value={5}>Show 5</option>
                                <option value={10}>Show 10</option>
                                <option value={20}>Show 20</option>
                            </select>
                            <div className='relative bg-white rounded-xl'>
                                <input
                                    className='pl-5 pr-10 py-2 outline-none text-black rounded-xl'
                                    type='search'
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder='Search by name or ID...'
                                />
                                <FontAwesomeIcon icon={faMagnifyingGlass} className='absolute text-[#5C1FE2] top-1/2 -translate-y-1/2 right-3' />
                            </div>
                        </div>
                    </div>

                    <div className='relative bg-white/10 mt-3 rounded-xl'>
                        {loading ? (
                            <div className='p-5 text-center text-white'>Loading reports...</div>
                        ) : (
                            <div className='overflow-x-auto'>
                                <table className='w-full text-center'>
                                    <thead>
                                        <tr className='border-b border-white/25'>
                                            <th className='font-semibold px-3 py-5'>Player ID</th>
                                            <th className='font-semibold px-3 py-5'>Email</th>
                                            <th className='font-semibold px-3 py-5'>Kit ID</th>
                                            <th className='font-semibold px-3 py-5'>Category</th>
                                            <th className='font-semibold px-3 py-5'>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-nowrap divide-y divide-white/25 border-b border-white/25'>
                                        {currentData.length > 0 ? currentData.map((item, index) => (
                                            <tr key={index}>
                                                <td className='py-3'>{item.user_id}</td>
                                                <td className='py-3'>{item.email}</td>
                                                <td className='py-3'>{item.extracted_id}</td>
                                                <td className='py-3 capitalize'>{item.category}</td>
                                                <td className='py-3 pr-5'>
                                                    <button className='px-4 py-2 rounded-full bg-[#5C1FE2] text-sm'>View Report</button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={5} className='py-5 text-white text-center'>No matching results</td>
                                            </tr>
                                        )}

                                        {[...Array(emptyRows)].map((_, index) => (
                                            <tr key={`empty-${index}`}>
                                                <td colSpan={5} className="text-center text-muted py-3">—</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className='mt-5 pb-5 px-5 flex justify-between items-center'>
                            <button
                                onClick={handlePrev}
                                className='px-3 py-1 rounded-lg border border-white cursor-pointer'
                                disabled={currentPage === 1}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <span>Page {currentPage} of {totalPages || 1}</span>
                            <button
                                onClick={handleNext}
                                className='px-3 py-1 rounded-lg border border-white cursor-pointer'
                                disabled={currentPage === totalPages || totalPages === 0}
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
